import { z } from 'zod';
import { roleSchema } from './api.ts';
import { LOCALE_CODES } from './settings.ts';

// Account provisioning — how the staff app asks the tenant API to create an account. Sign-up is
// closed by default (docs/requirements.md §19 #49): an account exists only because a staff owner
// made it. The staff app never touches the login tables; it sends this request, with a shared
// token, to the service that owns them.

/** Where the API serves it. Not under `/api`: the edge must not route this path from outside. */
export const PROVISIONING_PATH = '/internal/accounts';

// eslint-disable-next-line no-control-regex -- matching control characters is the point
const plainName = z.string().trim().min(1).max(100).regex(/^[^\u0000-\u001f\u007f]+$/u, 'must be one line of text');

export const provisionWorkspaceSchema = z.discriminatedUnion('kind', [
  /** An account and nothing else: a tenant admin invites it later. */
  z.object({ kind: z.literal('none') }),
  /** Adds the account to a workspace that exists. One that has no owner yet takes only an owner. */
  z.object({ kind: z.literal('existing'), tenantId: z.uuid(), role: roleSchema }),
  /** Creates a workspace with the account as its owner. */
  z.object({ kind: z.literal('new'), name: plainName, slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/u, 'must be lowercase letters, digits and dashes') }),
]);
export type ProvisionWorkspace = z.infer<typeof provisionWorkspaceSchema>;

export const provisionAccountSchema = z.object({
  email: z.email().max(254),
  name: plainName,
  /** The language of the "set your password" email. */
  locale: z.enum(LOCALE_CODES).default('en'),
  workspace: provisionWorkspaceSchema,
});
export type ProvisionAccountInput = z.infer<typeof provisionAccountSchema>;

export const provisionAccountResultSchema = z.object({
  userId: z.string(),
  /**
   * `true` — a new account, and a "set your password" email is on its way. `false` — the address
   * already had an account, which was only added to the workspace; nothing was sent.
   */
  created: z.boolean(),
  /** The workspace the account was put in, if any. */
  tenantId: z.uuid().nullable(),
});
export type ProvisionAccountResult = z.infer<typeof provisionAccountResultSchema>;

/**
 * Refusals, with their HTTP statuses: `unauthorized` 401 (no token, or a wrong one), `invalid` 422
 * (the body does not match {@link provisionAccountSchema}), `not_found` 404 (no such workspace),
 * `conflict` 409 (the account exists and there is nothing to add, it is already a member, the
 * workspace address is taken, or a workspace without an owner was given a lesser role).
 *
 * A refusal means nothing was written — with one exception, which its message states: a new account
 * whose workspace was taken by someone else between the check and the write keeps its account and its
 * welcome email, and is answered `conflict`.
 */
export const PROVISION_ERROR_CODES = ['unauthorized', 'invalid', 'not_found', 'conflict'] as const;
export const provisionErrorSchema = z.object({ error: z.object({ code: z.enum(PROVISION_ERROR_CODES), message: z.string() }) });
export type ProvisionError = z.infer<typeof provisionErrorSchema>;
